import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Row, Col, Pagination } from 'react-bootstrap';
import Api from '../Services/Api';
import Alert from '../Services/Alert';
import ExcelService from '../Services/ExcelService';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as moment from 'moment';

const InactiveUsers = () => {
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [paymentDays, setPaymentDays] = useState(7);
    const [bidDays, setBidDays] = useState('');
    const [customDate, setCustomDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        fetchInactiveUsers();
    }, [page, limit, paymentDays, bidDays, customDate]);

    const fetchInactiveUsers = async () => {
        setIsLoading(true);
        try {
            const payload = {
                page,
                limit,
                search,
                filterDays: paymentDays,
                bidFilterDays: bidDays,
                customDate: customDate ? moment(customDate).format('YYYY-MM-DD') : null
            };

            const res = await Api.post('/users/inactive-list', payload);
            if (res.data.status) {
                setUsers(res.data.data.docs);
                setTotalPages(res.data.data.totalPages);
            } else {
                Alert.error(res.data.message);
            }
        } catch (error) {
            Alert.error(error.message);
        }
        setIsLoading(false);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchInactiveUsers();
    };

    const handleExport = async () => {
        // Fetch all data for export (no pagination limit)
        try {
            const payload = {
                page: 1,
                limit: 10000, // Fetch large batch
                search,
                filterDays: paymentDays,
                bidFilterDays: bidDays,
                customDate: customDate ? moment(customDate).format('YYYY-MM-DD') : null
            };
            const res = await Api.post('/users/inactive-list', payload);
            if (res.data.status) {
                const dataToExport = res.data.data.docs.map(user => ({
                    Name: user.fullname || user.username || 'N/A',
                    Mobile: user.mobile,
                    "Last Payment": user.lastPaymentDate ? moment(user.lastPaymentDate).format('DD/MM/YYYY hh:mm A') : 'Never',
                    "Last Bid": user.lastBidDate ? moment(user.lastBidDate).format('DD/MM/YYYY hh:mm A') : 'Never'
                }));
                ExcelService.exportToExcel(dataToExport, 'Inactive_Users');
            }
        } catch (error) {
            Alert.error("Failed to export: " + error.message);
        }
    };

    return (
        <section className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-flex align-items-center justify-content-between">
                                <h4 className="mb-0 font-size-18">Inactive Users List</h4>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <Form onSubmit={handleSearch} className="mb-3">
                                        <Row>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Payment Inactivity</Form.Label>
                                                    <Form.Control as="select" value={paymentDays} onChange={(e) => {
                                                        setPaymentDays(e.target.value);
                                                        setPage(1);
                                                        if (e.target.value !== 'custom') setCustomDate(null);
                                                    }}>
                                                        <option value="7">Last 7 Days</option>
                                                        <option value="15">Last 15 Days</option>
                                                        <option value="30">Last 30 Days</option>
                                                        <option value="never">Never Payment Attempt</option>
                                                        <option value="custom">Custom Date (Inactivity Since)</option>
                                                    </Form.Control>
                                                </Form.Group>
                                            </Col>

                                            {paymentDays === 'custom' && (
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>Select Date</Form.Label>
                                                        <DatePicker
                                                            selected={customDate}
                                                            onChange={date => setCustomDate(date)}
                                                            className="form-control"
                                                            placeholderText="Users inactive since..."
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            )}

                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Bid Inactivity (Optional)</Form.Label>
                                                    <Form.Control as="select" value={bidDays} onChange={(e) => {
                                                        setBidDays(e.target.value);
                                                        setPage(1);
                                                    }}>
                                                        <option value="">None</option>
                                                        <option value="7">Last 7 Days</option>
                                                        <option value="15">Last 15 Days</option>
                                                        <option value="30">Last 30 Days</option>
                                                        <option value="never">Never Bidded</option>
                                                    </Form.Control>
                                                </Form.Group>
                                            </Col>

                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Search</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Name / Mobile"
                                                        value={search}
                                                        onChange={(e) => setSearch(e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={12} className="mt-3 text-end">
                                                <Button variant="primary" type="submit" className="me-2">
                                                    <i className="bx bx-search-alt-2 font-size-16 align-middle me-2"></i> Search
                                                </Button>
                                                <Button variant="success" onClick={handleExport}>
                                                    <i className="bx bx-export font-size-16 align-middle me-2"></i> Export Excel
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Form>

                                    <div className="table-responsive">
                                        <Table striped bordered hover>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Name / Username</th>
                                                    <th>Mobile</th>
                                                    <th>Last Payment Attempt</th>
                                                    <th>Last Bid Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isLoading ? (
                                                    <tr><td colSpan="5" className="text-center">Loading...</td></tr>
                                                ) : users.length > 0 ? (
                                                    users.map((user, index) => (
                                                        <tr key={user._id}>
                                                            <td>{(page - 1) * limit + index + 1}</td>
                                                            <td>{user.fullname && user.fullname.trim() !== "" ? user.fullname : user.username}</td>
                                                            <td>{user.mobile_no || user.mobile}</td>
                                                            <td>{user.lastPaymentDate ? moment(user.lastPaymentDate).format('DD/MM/YYYY hh:mm A') : <span className="text-danger">Never</span>}</td>
                                                            <td>{user.lastBidDate ? moment(user.lastBidDate).format('DD/MM/YYYY hh:mm A') : 'Never'}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="5" className="text-center">No inactive users found for criteria.</td></tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>

                                    <div className="d-flex justify-content-end mt-3">
                                        <Pagination>
                                            <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 1} />
                                            <Pagination.Item disabled>{page} / {totalPages}</Pagination.Item>
                                            <Pagination.Next onClick={() => setPage(page + 1)} disabled={page === totalPages} />
                                        </Pagination>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default InactiveUsers;
